<template>
  <v-card class="mb-4" variant="outlined">
    <v-card-title class="text-primary weight-600 d-flex align-center">
      <v-icon icon="mdi-auto-fix" class="mr-2"></v-icon>
      Autofill from Documents
    </v-card-title>
    <v-card-text>
      <div class="text-body-2 mb-4">
        Read the referral, insurance and prescriber documents on this submission and fill in as much of the form below
        as possible. Extracted values are written straight into the empty fields of the form and highlighted here with
        their source, so you can check them — where the documents offered more than one value, click another to swap it
        in.
      </div>

      <!-- Extra files: used by the autofill job only, never saved to the submission -->
      <div class="text-subtitle-2 mb-1">Additional files for autofill</div>
      <div class="text-body-2 text-medium-emphasis mb-2">
        Screenshots or PDFs that only help the autofill — for example the insurance, prescriber or BIN/PCN screens.
        Paste a screenshot with the Paste Screenshot button or Ctrl+V, or select as many files as you like at once.
        These are <strong>not</strong> attached to the submission as supporting documents unless you tick "Add to
        supplemental docs". To attach any other document to the submission itself, use the Supporting Documents section
        below.
      </div>

      <v-table v-if="extraDocs.length > 0" density="compact" class="mb-2">
        <tbody>
          <tr v-for="(doc, index) in extraDocs" :key="doc.link">
            <td>
              <v-icon icon="mdi-file-document-outline" size="18" class="mr-1"></v-icon>
              {{ doc.fileName }}
            </td>
            <td>
              <v-checkbox
                density="compact"
                hide-details
                label="Add to supplemental docs"
                :model-value="isAttached(doc)"
                :disabled="props.isRunning"
                @update:model-value="
                  (checked) => emit(checked ? 'attach' : 'detach', { fileName: doc.fileName, link: doc.link })
                " />
            </td>
            <td class="text-right">
              <v-btn
                variant="text"
                color="primary"
                size="small"
                prepend-icon="mdi-download"
                :href="doc.viewUrl || doc.link"
                target="_blank">
                View
              </v-btn>
              <v-btn
                variant="text"
                color="error"
                size="small"
                icon="mdi-close"
                :disabled="props.isRunning"
                @click="removeExtraDoc(index)"></v-btn>
            </td>
          </tr>
        </tbody>
      </v-table>

      <UploadFileComponent
        ref="uploaderRef"
        hide-file-name
        multiple
        allow-paste
        paste-shortcut
        :max-files="remainingExtraDocSlots"
        :disabled="props.isRunning || extraDocsFull"
        @download-link="addExtraDoc" />
      <v-alert v-if="extraDocsFull" type="info" variant="tonal" density="compact" class="mt-2">
        Autofill reads at most {{ AUTOFILL_MAX_EXTRA_DOCUMENTS }} additional files. Remove one to add another.
      </v-alert>
      <v-alert v-if="droppedFileName" type="warning" variant="tonal" density="compact" class="mt-2">
        {{ droppedFileName }} was not added: the list filled up while it was uploading. Remove a file and add it again.
      </v-alert>
      <v-alert v-if="documentsOverCap > 0" type="warning" variant="tonal" density="compact" class="mt-2">
        This submission has {{ totalDocCount }} documents in total, and autofill reads at most
        {{ AUTOFILL_MAX_DOCUMENTS_PER_RUN }} of them — {{ documentsOverCap }} will not be read. Remove the ones autofill
        doesn't need, or run it with fewer supporting documents attached.
      </v-alert>

      <v-divider class="my-4" />

      <div class="d-flex align-center flex-wrap ga-4">
        <v-btn
          color="primary"
          prepend-icon="mdi-auto-fix"
          :disabled="totalDocCount === 0 || props.isRunning"
          :loading="props.isRunning"
          @click="emit('run', { extraDocs: [...unattachedExtraDocs] })">
          Autofill from Documents
        </v-btn>
        <v-btn
          v-if="runStarted"
          variant="outlined"
          color="primary"
          prepend-icon="mdi-open-in-new"
          @click="emit('startAnother')">
          Start Another Submission
          <v-tooltip activator="parent" location="top" max-width="320">
            Opens a blank submission in a new tab. This draft and its autofill stay in this tab, so come back here to
            review and create it.
          </v-tooltip>
        </v-btn>
        <span v-if="props.status === AUTOFILL_STATUS.QUEUED" class="text-body-2 text-medium-emphasis">
          Queued — waiting for a worker to pick this up.
        </span>
        <span v-else-if="props.status === AUTOFILL_STATUS.PROCESSING" class="text-body-2 text-medium-emphasis">
          Reading the documents. This usually takes under a minute.
        </span>
        <span v-else-if="totalDocCount === 0" class="text-body-2 text-medium-emphasis">
          Upload a document above, or add one under Supporting Documents, to enable autofill.
        </span>
        <span v-else class="text-body-2 text-medium-emphasis">
          Will read {{ props.supportingDocsCount }} supporting
          {{ props.supportingDocsCount === 1 ? "document" : "documents" }} and
          {{ unattachedExtraDocs.length }} additional {{ unattachedExtraDocs.length === 1 ? "file" : "files" }}.
        </span>
      </div>

      <v-alert v-if="props.error" type="warning" variant="tonal" density="compact" class="mt-4">
        {{ props.error }}
      </v-alert>
      <v-alert
        v-else-if="props.status === AUTOFILL_STATUS.DONE && props.candidateCount > 0"
        type="success"
        variant="tonal"
        density="compact"
        class="mt-4">
        Found values for {{ props.candidateCount }} {{ props.candidateCount === 1 ? "field" : "fields" }} and filled the
        empty ones in — each applied value is marked green below. Click another value to swap it in.
      </v-alert>

      <template v-if="props.candidates && props.candidateCount > 0">
        <v-divider class="my-4" />
        <AutofillReviewPanel
          :candidates="props.candidates"
          :diagnoses="props.diagnoses"
          :order-info="props.orderInfo"
          @apply="(payload) => emit('apply', payload)"
          @apply-diagnosis="(payload) => emit('applyDiagnosis', payload)" />
      </template>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import UploadFileComponent from "@/components/UploadFileComponent";
import AutofillReviewPanel from "@/components/AutofillReviewPanel.vue";
import { AUTOFILL_MAX_DOCUMENTS_PER_RUN, AUTOFILL_MAX_EXTRA_DOCUMENTS, AUTOFILL_STATUS } from "@/constants/autofill";
import { s3KeyOf } from "@/utils/s3Links";

const props = defineProps({
  // Number of documents already attached to the submission (patient chart + supplemental).
  supportingDocsCount: {
    type: Number,
    default() {
      return 0;
    },
  },
  isRunning: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // One of AUTOFILL_STATUS — mirrors the poll endpoint's status.
  status: {
    type: String,
    default() {
      return AUTOFILL_STATUS.IDLE;
    },
  },
  error: {
    type: String,
    default() {
      return "";
    },
  },
  candidateCount: {
    type: Number,
    default() {
      return 0;
    },
  },
  // Per-field candidates from the last run, keyed by orderInfo field name.
  candidates: {
    type: Object,
    default() {
      return null;
    },
  },
  // Diagnosis candidates, kept separate because they are (name, code) pairs and the order
  // takes one primary plus any number of secondaries.
  diagnoses: {
    type: Array,
    default() {
      return [];
    },
  },
  orderInfo: {
    type: Object,
    default() {
      return {};
    },
  },
  // Autofill-only uploads recovered from a previous run: [{ url, fileName }]. Arrives after the
  // order loads, so it seeds extraDocs via a watcher rather than as an initial value.
  restoredDocs: {
    type: Array,
    default() {
      return [];
    },
  },
  // Links of every document already on the submission (patient chart + supplemental), so a row
  // here can show whether its file is attached too.
  attachedLinks: {
    type: Array,
    default() {
      return [];
    },
  },
});

const emit = defineEmits(["run", "apply", "applyDiagnosis", "attach", "detach", "startAnother"]);

const runStarted = computed(() =>
  [AUTOFILL_STATUS.QUEUED, AUTOFILL_STATUS.PROCESSING, AUTOFILL_STATUS.DONE].includes(props.status)
);

// Autofill-only uploads. Deliberately local state — these must never reach orderInfo.patientChart
// or supplementalDocs, or they would be saved as supporting documents on the submission.
const extraDocs = ref([]);
const uploaderRef = ref(null);
// A file whose upload finished after the list had filled up (an attached row was unticked
// meanwhile). Named so the user knows which one to redo.
const droppedFileName = ref("");

// Seed from a restored run once it arrives. Only fills an empty list, so it can never clobber
// files the user picked while the order was still loading.
watch(
  () => props.restoredDocs,
  (docs) => {
    if (extraDocs.value.length > 0 || !docs?.length) return;
    // link is the stored URL (what a re-run sends back and the API will accept); viewUrl is a
    // fresh presigned link, since the stored one has likely expired by now.
    extraDocs.value = docs.map((doc) => ({ fileName: doc.fileName, link: doc.url, viewUrl: doc.viewUrl }));
  },
  { immediate: true }
);

// Compared by S3 key: the submission's links are re-presigned on every load.
const attachedKeys = computed(() => new Set(props.attachedLinks.map(s3KeyOf)));
const isAttached = (doc) => attachedKeys.value.has(s3KeyOf(doc.link));
// An attached file is already among the supporting documents the run reads, so sending it again
// as an extra would read the same object twice against the per-run cap.
const unattachedExtraDocs = computed(() => extraDocs.value.filter((doc) => !isAttached(doc)));

const totalDocCount = computed(() => props.supportingDocsCount + unattachedExtraDocs.value.length);
// Capacity follows what the run sends: an attached row rides along as a supporting document and
// takes no extra-file slot.
const extraDocsFull = computed(() => unattachedExtraDocs.value.length >= AUTOFILL_MAX_EXTRA_DOCUMENTS);
const remainingExtraDocSlots = computed(() =>
  Math.max(0, AUTOFILL_MAX_EXTRA_DOCUMENTS - unattachedExtraDocs.value.length)
);
// The run itself is capped across supporting documents and these uploads together, so a
// submission with plenty of documents can go over even with room left in the uploader.
const documentsOverCap = computed(() => Math.max(0, totalDocCount.value - AUTOFILL_MAX_DOCUMENTS_PER_RUN));

function addExtraDoc(downloadLinkInfo) {
  // UploadFileComponent rejects an over-cap selection before uploading, but the cap can also be
  // reached while an upload is in flight. Say so rather than drop the file silently.
  if (extraDocsFull.value) {
    droppedFileName.value = downloadLinkInfo.fileName;
    uploaderRef.value?.forgetPaste(downloadLinkInfo.fileName);
    return;
  }
  droppedFileName.value = "";
  extraDocs.value.push({
    fileName: downloadLinkInfo.fileName,
    link: downloadLinkInfo.link,
    uploadKey: downloadLinkInfo.uploadKey,
  });
}

function removeExtraDoc(index) {
  const [removed] = extraDocs.value.splice(index, 1);
  // The checkbox goes with the row, so an attached copy would otherwise be stuck on the submission
  // with nothing left to untick.
  if (removed && isAttached(removed)) emit("detach", { fileName: removed.fileName, link: removed.link });
  // The file the user removed has to be pasteable again — the uploader would otherwise still see it
  // as already pasted. Named, so the files still in the list stay deduplicated.
  if (removed?.fileName) uploaderRef.value?.forgetPaste(removed.fileName);
}

defineExpose({ extraDocs });
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";
</style>
